import classNames from "classnames";
import { Avatar } from "./Avatar";
import { PrioChip, StatusDot } from "./chips";
import { formatDay, isOverdue } from "../lib/date";
import { STATUS, type Priority, type Status } from "../lib/design";

export interface TaskRowVM {
  id: string;
  title: string;
  /** Ref courte affichable — dérivée de l'id (pas de ref backend). */
  ref?: string;
  description?: string;
  status: Status;
  priority: Priority;
  dueKey?: string;
  /** Jour de création — début de barre du layout Timeline. */
  createdKey?: string;
  listName?: string;
  tags: string[];
  driverName?: string;
  subtaskCount?: number;
  assignees?: { ref: string; name: string }[];
  projectName?: string;
  /** Miroir répliqué (work item PM) — lecture seule, badge de provenance. */
  replicated?: boolean;
}

interface Props {
  task: TaskRowVM;
  selected: boolean;
  onSelect: (id: string) => void;
  onToggleDone: (task: TaskRowVM) => void;
  /** Propriétés masquées par la vue (panneau « Propriétés »). */
  hiddenProps?: string[];
}

/** Ligne de tâche riche — densité du design Todo DECO : titre + ref + avatars,
 * puis ligne meta (statut, priorité, échéance, liste, projet, badges). */
export function TaskRow({
  task,
  selected,
  onSelect,
  onToggleDone,
  hiddenProps,
}: Props) {
  const done = task.status === "done";
  const show = (p: string) => !hiddenProps?.includes(p);
  const avatars = show("assignees")
    ? (task.assignees ??
      (task.driverName
        ? [{ ref: task.driverName, name: task.driverName }]
        : []))
    : [];
  return (
    <div
      className={classNames("task-row", "rich", { selected })}
      onClick={() => onSelect(task.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onSelect(task.id)}
    >
      {task.replicated ? (
        <span
          className="task-check replicated"
          title="Work item PM — lecture seule"
        >
          ◆
        </span>
      ) : (
        <button
          className={classNames("task-check", { done })}
          aria-label={done ? "Rouvrir" : "Terminer"}
          onClick={(e) => {
            e.stopPropagation();
            onToggleDone(task);
          }}
        >
          {done ? "✓" : ""}
        </button>
      )}
      <div className="task-row-body">
        <div className="task-row-main">
          <span className={classNames("task-title", { done })}>
            {task.title}
          </span>
          {task.ref && <span className="task-ref mono">{task.ref}</span>}
          {avatars.length > 0 && (
            <span className="avatar-stack" style={{ marginLeft: "auto" }}>
              {avatars.slice(0, 4).map((a) => (
                <Avatar key={a.ref} name={a.name} size={20} />
              ))}
            </span>
          )}
        </div>
        <div className="task-meta">
          {show("status") && (
            <span className="task-meta-status">
              <StatusDot status={task.status} />
              {STATUS[task.status].label}
            </span>
          )}
          {show("priority") && <PrioChip prio={task.priority} />}
          {show("due") && task.dueKey && (
            <span
              className={classNames("task-due", {
                overdue: !done && isOverdue(task.dueKey),
              })}
            >
              ◷ {formatDay(task.dueKey)}
            </span>
          )}
          {show("list") && task.listName && (
            <span className="tag">{task.listName}</span>
          )}
          {show("project") && task.projectName && (
            <span className="tag">◆ {task.projectName}</span>
          )}
          {task.replicated && (
            <span
              className="chip"
              style={{
                color: "var(--violet)",
                background: "var(--violet-soft)",
              }}
            >
              ◆ PM
            </span>
          )}
          {show("subtasks") && task.subtaskCount ? (
            <span className="tag" title="Sous-tâches">
              ⌄ {task.subtaskCount}
            </span>
          ) : null}
          {show("tags") &&
            task.tags.slice(0, 3).map((t) => (
              <span key={t} className="tag">
                {t}
              </span>
            ))}
        </div>
      </div>
    </div>
  );
}
